/* eslint-disable no-magic-numbers */
import { useCallback } from 'react';

import { TreeDragDropHandlers, useTreeDragDrop } from '@futrli/ui-components/components/draggable-tree-grid';

import { createTreeFromIndex, duplicateTree, flattenTree, getChildIds } from '../../utils/driver-tree';
import { NonFinancialDriverTree, NonFinancialDriverTreeNode } from '../../types';

type MoveBoundary = 'top' | 'bottom';

interface MoveToBoundaryPosition {
  sourceIndex: number;
  targetIndex: number;
}

/**
 * Resolves a driver's current position and the requested edge of its existing parent.
 * Root drivers use the root list, while nested drivers and groups use their parent's children.
 *
 * @param tree Current non-financial drivers tree.
 * @param nodeId Driver or group to inspect.
 * @param boundary Parent edge that the node should move to.
 * @returns The current and target sibling indexes, or undefined when the node or parent cannot be located.
 */
const getMoveToBoundaryPosition = (
  tree: NonFinancialDriverTree,
  nodeId: string,
  boundary: MoveBoundary
): MoveToBoundaryPosition | undefined => {
  const node = tree.index[nodeId];
  const siblings = node?.parentId ? tree.index[node.parentId]?.children : tree.tree;

  if (!node || !siblings) {
    return;
  }

  const sourceIndex = siblings.findIndex(sibling => sibling.id === nodeId);
  if (sourceIndex < 0) {
    return;
  }

  return {
    sourceIndex,
    targetIndex: boundary === 'top' ? 0 : siblings.length - 1
  };
};

/** Returns whether moving a driver to the requested parent edge would change its position. */
const canMoveToBoundary = (position: MoveToBoundaryPosition | undefined, boundary: MoveBoundary) =>
  !!position &&
  (boundary === 'top' ? position.sourceIndex > position.targetIndex : position.sourceIndex < position.targetIndex);

/** Rebuilds the driver tree and normalizes the order stored in its index. */
const rebuildTree = (tree: NonFinancialDriverTree): NonFinancialDriverTree => {
  tree.tree = createTreeFromIndex(tree.index);
  flattenTree(tree.tree).forEach(node => {
    tree.index[node.id].order = node.order;
  });
  return tree;
};

export interface DriverDragHandlers extends TreeDragDropHandlers {
  /** Returns whether the row has a later sibling within its current parent. */
  canMoveToBottom: (index: number) => boolean;
  /** Returns whether the row has an earlier sibling within its current parent. */
  canMoveToTop: (index: number) => boolean;
}

interface UseDriversDragDropParams {
  flatData: NonFinancialDriverTreeNode[];
  draftTree: NonFinancialDriverTree;
  onDragAndDropUpdate: (tree: NonFinancialDriverTree) => void;
  onManualMoveUpdate?: (tree: NonFinancialDriverTree) => void;
  isUpdating: boolean;
}

const applyManualMoveUp = (
  dragNode: NonFinancialDriverTreeNode,
  targetNode: NonFinancialDriverTreeNode,
  _index: Record<string, NonFinancialDriverTreeNode>
) => {
  if (targetNode.isGroup && dragNode.parentId === targetNode.id) {
    // Moving up from inside a group onto the group itself → leave the group, place before it
    dragNode.parentId = targetNode.parentId;
    dragNode.order = targetNode.order - 0.5;
  } else if (targetNode.isGroup) {
    // Moving up onto a group from outside → enter as last child
    dragNode.parentId = targetNode.id;
    dragNode.order = Number.MAX_SAFE_INTEGER;
  } else if (targetNode.parentId && targetNode.parentId !== dragNode.parentId) {
    // Moving up into a different group → enter as last child
    dragNode.parentId = targetNode.parentId;
    dragNode.order = targetNode.order + 0.5;
  } else {
    // Same parent → simple reorder
    dragNode.order = targetNode.order - 0.5;
    dragNode.parentId = targetNode.parentId;
  }
};

const applyManualMoveDown = (
  dragNode: NonFinancialDriverTreeNode,
  targetNode: NonFinancialDriverTreeNode,
  index: Record<string, NonFinancialDriverTreeNode>
) => {
  if (dragNode.parentId && dragNode.parentId !== targetNode.parentId) {
    // Moving down out of a group → place after the group
    const parentNode = index[dragNode.parentId];
    dragNode.parentId = parentNode ? parentNode.parentId : targetNode.parentId;
    dragNode.order = parentNode ? parentNode.order + 0.5 : targetNode.order + 0.5;
  } else if (targetNode.isGroup && !dragNode.isGroup) {
    // Moving down onto a group from outside → enter as first child
    dragNode.parentId = targetNode.id;
    dragNode.order = -1;
  } else {
    // Same parent → simple reorder
    dragNode.order = targetNode.order + 0.5;
    dragNode.parentId = targetNode.parentId;
  }
};

export const useDriversDragDrop = ({
  flatData,
  draftTree,
  onDragAndDropUpdate,
  onManualMoveUpdate,
  isUpdating
}: UseDriversDragDropParams): DriverDragHandlers => {
  const isDragInvalid = useCallback(
    (dragIndex: number, overIndex: number) => {
      const dragContent = flatData[dragIndex];
      const overContent = flatData[overIndex];

      if (!dragContent || !overContent) {
        return true;
      }
      if (dragContent.id === overContent.id) {
        return true;
      }

      if (dragContent.isGroup) {
        const childIds = getChildIds(dragContent.children);
        if (childIds.includes(overContent.id)) {
          return true;
        }
      }

      return false;
    },
    [flatData]
  );

  const calculateNewTree = useCallback(
    (dragIndex: number, dropIndex: number, direction?: 'up' | 'down'): NonFinancialDriverTree | undefined => {
      if (isDragInvalid(dragIndex, dropIndex)) {
        return undefined;
      }

      const newTree = duplicateTree(draftTree.index);
      const dragNode = newTree.index[flatData[dragIndex].id];
      const targetNode = newTree.index[flatData[dropIndex].id];

      if (!dragNode || !targetNode) {
        return undefined;
      }

      if (direction === 'up') {
        applyManualMoveUp(dragNode, targetNode, newTree.index);
      } else if (direction === 'down') {
        applyManualMoveDown(dragNode, targetNode, newTree.index);
      } else if (targetNode.isGroup && !dragNode.isGroup) {
        // Drag-and-drop: drop on a group → add as first child
        dragNode.parentId = targetNode.id;
        dragNode.order = -1;
      } else {
        // Drag-and-drop: set new order based on relative position
        dragNode.order = dragIndex < dropIndex ? targetNode.order + 0.5 : targetNode.order - 0.5;
        dragNode.parentId = targetNode.parentId;
      }

      return rebuildTree(newTree);
    },
    [draftTree, flatData, isDragInvalid]
  );

  const canMoveToTreeBoundary = useCallback(
    (index: number, boundary: MoveBoundary) => {
      const item = flatData[index];
      if (!item) {
        return false;
      }
      return canMoveToBoundary(getMoveToBoundaryPosition(draftTree, item.id, boundary), boundary);
    },
    [draftTree, flatData]
  );

  /** Determines whether move-to-top would change the row's position without changing its parent. */
  const canMoveToTop = useCallback((index: number) => canMoveToTreeBoundary(index, 'top'), [canMoveToTreeBoundary]);

  /** Determines whether move-to-bottom would change the row's position without changing its parent. */
  const canMoveToBottom = useCallback(
    (index: number) => canMoveToTreeBoundary(index, 'bottom'),
    [canMoveToTreeBoundary]
  );

  /**
   * Moves a driver or group to an edge of its current parent while preserving its parent ID
   * and descendants. Setting an outlying order lets the standard rebuild normalize all siblings.
   */
  const moveToTreeBoundary = useCallback(
    (index: number, boundary: MoveBoundary) => {
      const item = flatData[index];
      if (isUpdating || !item || !canMoveToTreeBoundary(index, boundary)) {
        return;
      }

      const newTree = duplicateTree(draftTree.index);
      const dragNode = newTree.index[item.id];
      if (!dragNode) {
        return;
      }

      dragNode.order = boundary === 'top' ? -1 : Number.MAX_SAFE_INTEGER;
      (onManualMoveUpdate || onDragAndDropUpdate)(rebuildTree(newTree));
    },
    [canMoveToTreeBoundary, draftTree, flatData, isUpdating, onDragAndDropUpdate, onManualMoveUpdate]
  );

  /** Moves a driver or group to the beginning of its current parent. */
  const onMoveToTop = useCallback((index: number) => moveToTreeBoundary(index, 'top'), [moveToTreeBoundary]);

  /** Moves a driver or group to the end of its current parent. */
  const onMoveToBottom = useCallback((index: number) => moveToTreeBoundary(index, 'bottom'), [moveToTreeBoundary]);

  const dragHandlers = useTreeDragDrop<NonFinancialDriverTree>({
    dataLength: flatData.length,
    isDragInvalid,
    calculateNewTree,
    onDragComplete: onDragAndDropUpdate,
    onManualMoveComplete: onManualMoveUpdate,
    canManualMove: !isUpdating
  });

  return { ...dragHandlers, canMoveToBottom, canMoveToTop, onMoveToBottom, onMoveToTop };
};
